import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {shareReplay, tap, catchError} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {SingleResult, PaginatedResult} from '@app/interfaces';
import {FinaConfigLevelEnum} from '@app/enums/FinaConfigLevelEnum';

export enum EInvoiceStatus {
  // Incoming invoice statuses
  PENDING = 'PENDING',
  RECEIVED = 'RECEIVED',
  RECEIVING_CONFIRMED = 'RECEIVING_CONFIRMED',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  PAYMENT_RECEIVED = 'PAYMENT_RECEIVED',
  PAYMENT_FULFILLED = 'PAYMENT_FULFILLED',
  PAYMENT_PARTIALLY_FULFILLED = 'PAYMENT_PARTIALLY_FULFILLED',

  // Outgoing invoice statuses
  SENT = 'SENT',
  DELIVERED = 'DELIVERED',
  ACCEPTED = 'ACCEPTED',
  FAILED = 'FAILED',
  ERROR = 'ERROR',
  NOT_SENT = 'NOT_SENT',
  REPORTED_UNDELIVERABLE = 'REPORTED_UNDELIVERABLE'
}

export type EInvoiceDirection = 'incoming' | 'outgoing';

export class EInvoiceStatusHelper {
  static getStatusDirection(status: EInvoiceStatus): EInvoiceDirection {
    const incomingStatuses = [
      EInvoiceStatus.PENDING,
      EInvoiceStatus.RECEIVED,
      EInvoiceStatus.RECEIVING_CONFIRMED,
      EInvoiceStatus.APPROVED,
      EInvoiceStatus.REJECTED,
      EInvoiceStatus.PAYMENT_RECEIVED,
      EInvoiceStatus.PAYMENT_FULFILLED,
      EInvoiceStatus.PAYMENT_PARTIALLY_FULFILLED
    ];
    return incomingStatuses.includes(status) ? 'incoming' : 'outgoing';
  }
}

export enum EInvoicePaymentStatus {
  UNPAID = 'unpaid',
  PARTIALLY_PAID = 'partially_paid',
  PAID = 'paid',
  OVERPAID = 'overpaid'
}

export interface EInvoicePayment {
  id: string;
  einvoice_incoming_id: string;
  amount: number;
  type: 'bank' | 'online' | 'paypal' | 'card' | 'crypto' | 'coupon' | 'cash' | 'other';
  date: string;
  note?: string;
  created_by_user_id?: string;
  created_at: string;
  updated_at: string;
}

export interface EInvoicePaymentMeta {
  total_paid: number;
  total_amount: number;
  remaining_amount: number;
  payment_status: EInvoicePaymentStatus;
  is_fully_paid: boolean;
}

export interface EInvoiceIncomingInvoice {
  id: string;
  account_uuid: string;
  provider_code: string;
  transaction_type: string;
  provider_invoice_id: string;
  sender_tax_number: string;
  sender_name: string;
  sender_vat_number: string | null;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  total_amount: string | number;
  net_amount: string | number | null;
  tax_amount: string | number | null;
  currency: string;
  description: string | null;
  status: EInvoiceStatus | string;
  document_type_code: string | null;
  document_type_text: string | null;
  received_at: string;
  deadline_at: string;
  responded_at: string | null;
  response_reason: string | null;
  partially_paid_amount: string | number | null;
  partially_paid_at: string | null;
  paid_at: string | null;
  responded_by_user_id: string | null;
  provider_metadata: any;
  ubl_xml: string | null;
  parsed_line_items: any;
  sync_to_spaceinvoices: boolean;
  spaceinvoices_id: string | null;
  sync_status: string;
  sync_error: string | null;
  last_sync_attempt: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  archived_at?: string | null; // Archival timestamp (6-year retention requirement)
  // Payment tracking fields (calculated from payments relationship)
  payment_status?: EInvoicePaymentStatus;
  total_paid?: number;
  remaining_amount?: number;
  payments_count?: number;
  payments?: EInvoicePayment[];
  payment?: EInvoicePayment; // First payment (backward compatibility)
}

export interface EInvoiceLineItem {
  name: string;
  description?: string;
  quantity: number;
  unit_code: string;
  price: number;
  line_total: number;
  tax_percent: number;
}

export interface PaymentDetails {
  iban: string;
  bic: string | null;
  bank_name: string | null;
  supplier_name: string;
  amount: number;
  currency: string;
  invoice_number: string;
  reference: string | null;
}

export interface EInvoiceAttachment {
  index: number;
  filename: string;
  mime_type: string;
  size: number;
  is_original_invoice: boolean;
}

export interface EInvoiceAttachmentsMetadata {
  has_multiple: boolean;
  metadata: EInvoiceAttachment[];
  total_count: number;
  total_size: number;
}

export interface EInvoiceDetails {
  id: string;
  timestamp: string;
  status: string;
  invoice_number: string;
  issue_date: string;
  due_date: string;
  received_at: string;
  sender_name: string;
  sender_tax_number: string;
  total_amount: string | number;
  currency: string;
  line_items: EInvoiceLineItem[];
  has_pdf: boolean;
  pdf_base64?: string;
  xml_base64?: string;
  // Embedded PDF fields (original PDF from sender)
  has_embedded_pdf: boolean;
  embedded_pdf_base64?: string;
  embedded_pdf_filename?: string;
  embedded_pdf_size?: number;
  // Multiple attachments support
  attachments?: EInvoiceAttachmentsMetadata;
  // Croatian HUB3 payment QR code (FINA EUR invoices only)
  payment_qr_code: string | null;
  payment_details: PaymentDetails | null;
  has_payment_qr: boolean;
  // Payment tracking (loaded separately)
  payments?: EInvoicePayment[];
  total_paid?: number;
  remaining_amount?: number;
  payment_status?: EInvoicePaymentStatus;
  // Archival fields (6-year retention requirement)
  is_archived?: boolean;
  archived_at?: string | null;
  has_xml?: boolean;
  xml_download_url?: string;
  pdf_available?: boolean;
  // Vehicle-expense links (BRIEFD-6318) — returned inline to avoid an extra request
  vehicle_expense_links?: EInvoiceVehicleExpenseLinksResponse;
}

export interface EInvoiceSyncIncomingResponse {
  success: boolean;
  fina_response: {
    message: string;
    ack_status: string;
    ack_status_code: number;
  };
}

export interface EInvoiceOutgoingHistoryEntry {
  id: string;
  document_type_code: string | null;
  status: string;
  corrects_einvoice_id: string | null;
  sent_at: string | null;
  created_at: string;
  delivery_mode: string | null;
  error_message: string | null;
  rejection_reason: string | null;
}

export interface EInvoiceOutgoingInvoice {
  id: string;
  invoiceNumber: string;
  receiverName: string;
  receiverOib: string;
  date: string;
  amount: number;
  currency: string;
  status: 'SENT' | 'DELIVERED' | 'ACCEPTED' | 'REJECTED';
  sentAt: string;
  deliveredAt?: string;
  statusChangedAt?: string;
  finaMessageId?: string;
  spaceInvoiceId: string;
}

export interface EInvoiceListFilters {
  date_from?: string;
  date_to?: string;
  status?: string;
  provider_code?: string;
  sender_tax_number?: string;
  sender_name?: string;
  is_overdue?: boolean;
  is_urgent?: boolean;
  per_page?: number;
  page?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  // New filter parameters
  invoice_number?: string;
  search?: string;
  payment_status?: EInvoicePaymentStatus | '';
  invoice_type?: string;
  archived?: 'all' | 'archived' | 'not_archived';
}

export interface EInvoiceSyncRequest {
  date_from?: string;
  date_to?: string;
  status?: string; // Can be 'RECEIVED', 'NEW', 'ACCEPTED', 'REJECTED', etc.
  fetch_details?: boolean;
}

export interface EInvoiceSendRequest {
  account_uuid: string;
  space_invoice_document: any;
  supplier_data: any;
  receiver_oib: string;
}

export type CodeReasonType = 'VAT_REASON' | 'NOT_VAT_REASON' | 'OTHER_REASON';

export interface EInvoiceAcceptRejectRequest {
  account_uuid: string;
  reason?: string;
  code_reason?: CodeReasonType;
}


export interface EInvoiceCertificate {
  id: string;
  oib: string;
  subject_cn: string;
  valid_from: string;
  valid_to: string;
  is_expired: boolean;
  expires_soon: boolean;
}

export interface EInvoiceStatistics {
  outgoing: {
    total_invoices: number;
    successful_invoices: number;
    success_rate: number;
    pending_count: number;
    recent_activity: {
      last_30_days: number;
      last_sent_at: string;
      last_invoice_status: string;
    };
    status_breakdown: {
      pending: number;
      sending: number;
      sent: number;
      received: number;
      processing: number;
      confirmed: number;
      accepted: number;
      rejected: number;
      failed: number;
      error: number;
    };
  };
  incoming: {
    total_invoices: number;
    responded_invoices: number;
    response_rate: number;
    pending_count: number;
    urgent_count: number;
    overdue_count: number;
    recent_activity: {
      last_30_days: number;
      last_received_at: string;
      last_invoice_status: string;
    };
    average_response_time_hours: number;
    status_breakdown: {
      pending: number;
      accepted: number;
      rejected: number;
    };
  };
}

export interface FinaValidationError {
  code: string;
  title: string;
  message: string;
  steps: string[];
  technical_details?: string;
  help_articles: Array<{
    title: string;
    url: string;
  }>;
}

export interface FinaValidationResult {
  is_valid: boolean;
  checked_at: string;
  errors: FinaValidationError[];
  message?: string; // Optional message (e.g., "Configuration is working - you have already received invoices")
}

export interface EInvoiceConfigurationCheck {
  is_configured: boolean;
  requirements: {
    fina_b2b_feature_enabled: boolean;
    fiscalization_enabled: boolean;
    certificate_exists: boolean;
  };
  missing_requirements: string[];
  certificate?: EInvoiceCertificate;
  statistics?: EInvoiceStatistics;
  validation?: FinaValidationResult;
}

export interface EInvoiceOutgoingInvoiceStatus {
  success: boolean;
  status: string | null;
  einvoice_id: string | null;
  sent_at: string | null;
  error_message: string | null;
  recipient_oib: string;
  recipient_name: string;
  is_payment_locked?: boolean; // Payment locked until B2B invoice is accepted
}

export interface FinaBranch {
  endpoint_id: string;
  scheme_id: string;
  party_id: string;
  party_name: string;
  oib: string;
}

export interface ValidateRecipientResponse {
  oib: string;
  can_receive: boolean;
  recipient_name?: string;
  error_message?: string;
  branches?: FinaBranch[];
  should_auto_select?: boolean;
  recommended_branch?: FinaBranch | null;
}

export interface EInvoiceBadgeCounts {
  incoming_action_needed: number;
  outgoing_invoices_payment_needed: number;
  outgoing_advances_payment_needed: number;
  total: number;
}

@Injectable({
  providedIn: 'root'
})
export class EInvoiceApiService {
  private inFlightRequests = new Map<string, Observable<any>>();

  constructor(private http: HttpClient) {
  }

  /**
   * Fetch paginated list of incoming e-invoices with optional filtering and sorting
   * @param filters - Optional filters for status, dates, sender, pagination and sorting
   * @returns Observable of paginated invoice list with metadata
   */
  getIncomingInvoicesList(filters: EInvoiceListFilters = {}): Observable<PaginatedResult<EInvoiceIncomingInvoice[]>> {
    let params = new HttpParams();

    // Set defaults
    params = params.set('page', (filters.page || 1).toString());
    params = params.set('per_page', (filters.per_page || 20).toString());

    // Add optional filters
    if (filters.status && filters.status !== 'all') {
      params = params.set('status', filters.status);
    }

    if (filters.date_from) {
      params = params.set('date_from', filters.date_from);
    }

    if (filters.date_to) {
      params = params.set('date_to', filters.date_to);
    }

    if (filters.sender_name) {
      params = params.set('sender_name', filters.sender_name);
    }

    if (filters.sender_tax_number) {
      params = params.set('sender_tax_number', filters.sender_tax_number);
    }

    if (filters.provider_code) {
      params = params.set('provider_code', filters.provider_code);
    }

    if (filters.is_overdue !== undefined) {
      params = params.set('is_overdue', filters.is_overdue.toString());
    }

    if (filters.is_urgent !== undefined) {
      params = params.set('is_urgent', filters.is_urgent.toString());
    }

    if (filters.sort_by) {
      params = params.set('sort_by', filters.sort_by);
    }

    if (filters.sort_order) {
      params = params.set('sort_order', filters.sort_order);
    }

    // New filter parameters
    if (filters.invoice_number) {
      params = params.set('invoice_number', filters.invoice_number);
    }

    if (filters.search) {
      params = params.set('search', filters.search);
    }

    if (filters.payment_status) {
      params = params.set('payment_status', filters.payment_status);
    }

    if (filters.invoice_type && filters.invoice_type !== 'all') {
      params = params.set('invoice_type', filters.invoice_type);
    }

    if (filters.archived) {
      params = params.set('archived', filters.archived);
    }

    return this.http.get<PaginatedResult<EInvoiceIncomingInvoice[]>>(
      `${environment.apiUrl}/api/einvoice/incoming/list`,
      {params}
    );
  }

  /**
   * Confirm receipt of an incoming e-invoice (first step in invoice approval workflow)
   * @param invoiceId - FINA invoice ID
   * @param request - Account UUID for authorization
   * @returns Observable with confirmation result
   */
  confirmReceivingInvoice(invoiceId: string, request: EInvoiceAcceptRejectRequest): Observable<SingleResult<any>> {
    const requestKey = `confirm-${invoiceId}`;

    // If already in flight, return same observable (prevents duplicates)
    if (this.inFlightRequests.has(requestKey)) {
      return this.inFlightRequests.get(requestKey)!;
    }

    const request$ = this.http.post<SingleResult<any>>(
      `${environment.apiUrl}/api/einvoice/incoming/${invoiceId}/confirm-receiving`,
      request
    ).pipe(
      shareReplay(1),
      tap(() => this.inFlightRequests.delete(requestKey)),
      catchError(err => {
        this.inFlightRequests.delete(requestKey);
        return throwError(() => err);
      })
    );

    this.inFlightRequests.set(requestKey, request$);
    return request$;
  }

  /**
   * Accept an incoming e-invoice (final step after confirming receipt)
   * @param invoiceId - FINA invoice ID
   * @param request - Account UUID for authorization
   * @returns Observable with acceptance result
   */
  acceptIncomingInvoice(invoiceId: string, request: EInvoiceAcceptRejectRequest): Observable<SingleResult<any>> {
    const requestKey = `accept-${invoiceId}`;

    // If already in flight, return same observable (prevents duplicates)
    if (this.inFlightRequests.has(requestKey)) {
      return this.inFlightRequests.get(requestKey)!;
    }

    const request$ = this.http.post<SingleResult<any>>(
      `${environment.apiUrl}/api/einvoice/incoming/${invoiceId}/accept`,
      request
    ).pipe(
      shareReplay(1),
      tap(() => this.inFlightRequests.delete(requestKey)),
      catchError(err => {
        this.inFlightRequests.delete(requestKey);
        return throwError(() => err);
      })
    );

    this.inFlightRequests.set(requestKey, request$);
    return request$;
  }

  /**
   * Reject an incoming e-invoice with reason and category
   * @param invoiceId - FINA invoice ID
   * @param request - Contains account UUID, rejection reason and code reason (VAT/non-VAT/other)
   * @returns Observable with rejection result
   */
  rejectIncomingInvoice(invoiceId: string, request: EInvoiceAcceptRejectRequest): Observable<SingleResult<any>> {
    const requestKey = `reject-${invoiceId}`;

    // If already in flight, return same observable (prevents duplicates)
    if (this.inFlightRequests.has(requestKey)) {
      return this.inFlightRequests.get(requestKey)!;
    }

    const request$ = this.http.post<SingleResult<any>>(
      `${environment.apiUrl}/api/einvoice/incoming/${invoiceId}/reject`,
      request
    ).pipe(
      shareReplay(1),
      tap(() => this.inFlightRequests.delete(requestKey)),
      catchError(err => {
        this.inFlightRequests.delete(requestKey);
        return throwError(() => err);
      })
    );

    this.inFlightRequests.set(requestKey, request$);
    return request$;
  }

  /**
   * Perform full synchronization with FINA e-invoice system to fetch latest invoices
   * @param accountUuid - Optional account UUID (currently not used by API)
   * @returns Observable with synchronization response and FINA acknowledgment status
   */
  fullSync(accountUuid?: string): Observable<EInvoiceSyncIncomingResponse> {
    return this.http.post<EInvoiceSyncIncomingResponse>(
      `${environment.apiUrl}/api/einvoice/incoming/sync`,
      {}
    );
  }

  /**
   * Get e-invoice sending status for an outgoing invoice
   * @param spaceInvoiceId - SpaceInvoices document ID
   * @returns Observable with e-invoice status, timestamps, and error messages if any
   */
  getOutgoingInvoiceStatus(spaceInvoiceId: string): Observable<EInvoiceOutgoingInvoiceStatus> {
    return this.http.get<EInvoiceOutgoingInvoiceStatus>(
      `${environment.apiUrl}/api/einvoice/outgoing/status/${spaceInvoiceId}`
    );
  }

  /**
   * Get send history for an outgoing invoice (original + corrections)
   * @param documentId - Administration document ID (SpaceInvoices document ID)
   * @returns Observable with chronological list of all send entries
   */
  getOutgoingInvoiceHistory(documentId: string): Observable<SingleResult<EInvoiceOutgoingHistoryEntry[]>> {
    return this.http.get<SingleResult<EInvoiceOutgoingHistoryEntry[]>>(
      `${environment.apiUrl}/api/einvoice/outgoing/${documentId}/history`
    );
  }

  /**
   * Get detailed information about an incoming e-invoice
   * @param finaInvoiceId - FINA invoice ID
   * @param includePdf - Whether to include base64-encoded FINA PDF in response
   * @param includeEmbeddedPdf - Whether to include base64-encoded embedded PDF (from sender) in response
   * @returns Observable with invoice details, line items, and optional PDF/XML data
   */
  getIncomingInvoiceDetails(
    finaInvoiceId: string,
    includePdf: boolean = false,
    includeEmbeddedPdf: boolean = false
  ): Observable<SingleResult<EInvoiceDetails>> {
    let params = new HttpParams();
    if (includePdf) {
      params = params.set('include_pdf', 'true');
    }
    if (includeEmbeddedPdf) {
      params = params.set('include_embedded_pdf', 'true');
    }

    return this.http.get<SingleResult<EInvoiceDetails>>(
      `${environment.apiUrl}/api/einvoice/incoming/${finaInvoiceId}/details`,
      {params}
    );
  }

  /**
   * Download UBL XML file for an incoming invoice (6-year archival requirement)
   * @param finaInvoiceId - FINA invoice ID
   * @returns Observable with XML file as Blob
   */
  downloadIncomingInvoiceXml(finaInvoiceId: string): Observable<Blob> {
    return this.http.get(
      `${environment.apiUrl}/api/einvoice/incoming/${finaInvoiceId}/download-xml`,
      {responseType: 'blob'}
    );
  }

  /**
   * Download original embedded PDF from incoming invoice
   * Returns JSON with base64 content that can be converted to blob
   * @param finaInvoiceId - FINA invoice ID
   * @returns Observable with PDF data (base64 content, filename, mime type)
   */
  downloadOriginalPdf(finaInvoiceId: string): Observable<SingleResult<{content: string, filename: string, mime_type: string, size?: number}>> {
    return this.http.get<SingleResult<{content: string, filename: string, mime_type: string, size?: number}>>(
      `${environment.apiUrl}/api/einvoice/incoming/${finaInvoiceId}/download/original`
    );
  }

  /**
   * Download FINA-generated PDF from incoming invoice
   * Returns JSON with base64 content that can be converted to blob
   * @param finaInvoiceId - FINA invoice ID
   * @returns Observable with PDF data (base64 content, filename, mime type)
   */
  downloadFinaPdf(finaInvoiceId: string): Observable<SingleResult<{content: string, filename: string, mime_type: string, size?: number}>> {
    return this.http.get<SingleResult<{content: string, filename: string, mime_type: string, size?: number}>>(
      `${environment.apiUrl}/api/einvoice/incoming/${finaInvoiceId}/download/fina`
    );
  }

  /**
   * Download a specific attachment by index from incoming invoice
   * Returns JSON with base64 content that can be converted to blob
   * @param finaInvoiceId - FINA invoice ID
   * @param attachmentIndex - Index of the attachment to download
   * @returns Observable with attachment data (base64 content, filename, mime type)
   */
  downloadAttachment(finaInvoiceId: string, attachmentIndex: number): Observable<SingleResult<{content: string, filename: string, mime_type: string, size: number}>> {
    return this.http.get<SingleResult<{content: string, filename: string, mime_type: string, size: number}>>(
      `${environment.apiUrl}/api/einvoice/incoming/${finaInvoiceId}/attachments/${attachmentIndex}`
    );
  }

  /**
   * Download all attachments as a ZIP file from incoming invoice
   * @param finaInvoiceId - FINA invoice ID
   * @returns Observable with ZIP file as Blob
   */
  downloadAllAttachmentsZip(finaInvoiceId: string): Observable<SingleResult<{content: string, filename: string, mime_type: string, size: number}>> {
    return this.http.get<SingleResult<{content: string, filename: string, mime_type: string, size: number}>>(
      `${environment.apiUrl}/api/einvoice/incoming/${finaInvoiceId}/attachments/download-all`
    );
  }

  /**
   * @deprecated Use downloadOriginalPdf() instead
   * Get download URL for original embedded PDF from incoming invoice
   * @param finaInvoiceId - FINA invoice ID
   * @returns URL for downloading the original PDF
   */
  getDownloadOriginalPdfUrl(finaInvoiceId: string): string {
    return `${environment.apiUrl}/api/einvoice/incoming/${finaInvoiceId}/download/original`;
  }

  /**
   * @deprecated Use downloadFinaPdf() instead
   * Get download URL for FINA-generated PDF from incoming invoice
   * @param finaInvoiceId - FINA invoice ID
   * @returns URL for downloading the FINA PDF
   */
  getDownloadFinaPdfUrl(finaInvoiceId: string): string {
    return `${environment.apiUrl}/api/einvoice/incoming/${finaInvoiceId}/download/fina`;
  }

  getBadgeCounts(): Observable<SingleResult<EInvoiceBadgeCounts>> {
    return this.http.get<SingleResult<EInvoiceBadgeCounts>>(
      `${environment.apiUrl}/api/einvoice/badge-counts`
    );
  }

  checkConfiguration(): Observable<SingleResult<EInvoiceConfigurationCheck>> {
    return this.http.get<SingleResult<EInvoiceConfigurationCheck>>(
      `${environment.apiUrl}/api/einvoice/configuration`
    );
  }

  retryOutgoingInvoice(spaceInvoiceId: string, finaBranchData?: FinaBranch, einvoiceId?: string): Observable<SingleResult<any>> {
    const body: any = {};
    if (finaBranchData) {
      body.finaBranchData = {
        endpoint_id: finaBranchData.endpoint_id,
        scheme_id: finaBranchData.scheme_id,
        party_id: finaBranchData.party_id,
        party_name: finaBranchData.party_name
      };
    }
    if (einvoiceId) {
      body.einvoice_id = einvoiceId;
    }
    return this.http.post<SingleResult<any>>(
      `${environment.apiUrl}/api/einvoice/outgoing/resend/${spaceInvoiceId}`,
      body
    );
  }

  markOutgoingInvoicePaid(spaceInvoiceId: string, accountUuid: string): Observable<SingleResult<any>> {
    return this.http.post<SingleResult<any>>(
      `${environment.apiUrl}/api/einvoice/outgoing/${spaceInvoiceId}/mark-paid`,
      {account_uuid: accountUuid}
    );
  }

  /**
   * Validate if a recipient OIB is registered for B2B e-invoicing
   * @param oib - Croatian tax identification number
   * @returns Observable with validation result including can_receive status and branches if multiple exist
   */
  validateRecipient(oib: string): Observable<SingleResult<ValidateRecipientResponse>> {
    return this.http.post<SingleResult<ValidateRecipientResponse>>(
      `${environment.apiUrl}/api/integrations/fina/validate-recipient`,
      { oib }
    );
  }

  /**
   * Get sender's own branches from FINA using their certificate OIB
   * @returns Observable with list of sender's branches, auto-selection status, and auto-saved branch if applicable
   */
  getSenderBranches(): Observable<SingleResult<{branches: FinaBranch[], oib: string, auto_selected: boolean, auto_saved: boolean, selected_branch: FinaBranch | null}>> {
    return this.http.post<SingleResult<{branches: FinaBranch[], oib: string, auto_selected: boolean, auto_saved: boolean, selected_branch: FinaBranch | null}>>(
      `${environment.apiUrl}/api/integrations/fina/get-sender-branches`,
      {}
    );
  }

  /**
   * Save selected sender branch to account configuration or business unit
   * @param branch - Selected branch data to save
   * @param businessUnitUuid - Optional business unit UUID (if saving per-business-unit branch)
   * @returns Observable with save confirmation
   */
  saveSenderBranch(branch: FinaBranch, businessUnitUuid?: string): Observable<SingleResult<{message: string}>> {
    const payload: any = {
      endpoint_id: branch.endpoint_id,
      scheme_id: branch.scheme_id,
      party_id: branch.party_id,
      party_name: branch.party_name
    };

    if (businessUnitUuid) {
      payload.business_unit_uuid = businessUnitUuid;
    }

    return this.http.post<SingleResult<{message: string}>>(
      `${environment.apiUrl}/api/integrations/fina/save-sender-branch`,
      payload
    );
  }

  /**
   * Get saved sender branch configuration (account-level or business unit-specific)
   * @param businessUnitUuid - Optional business unit UUID to get BU-specific branch
   * @returns Observable with saved branch data, config_level matching FinaConfigLevelEnum values
   */
  getSenderBranch(businessUnitUuid?: string): Observable<SingleResult<{branch: FinaBranch | null, config_level: 'business_unit' | 'account' | 'none'}>> {
    let params = new HttpParams();
    if (businessUnitUuid) {
      params = params.set('business_unit_uuid', businessUnitUuid);
    }

    return this.http.get<SingleResult<{branch: FinaBranch | null, config_level: 'business_unit' | 'account' | 'none'}>>(
      `${environment.apiUrl}/api/integrations/fina/get-sender-branch`,
      { params }
    );
  }

  /**
   * Manually test FINA configuration (clears cache and performs fresh validation)
   * @returns Observable with fresh validation result
   */
  testConfiguration(): Observable<SingleResult<FinaValidationResult>> {
    return this.http.post<SingleResult<FinaValidationResult>>(
      `${environment.apiUrl}/api/einvoice/configuration/test`,
      {}
    );
  }

  /**
   * Clear FINA configuration validation cache
   * Forces next validation check to be fresh
   * @returns Observable with confirmation
   */
  clearValidationCache(): Observable<SingleResult<{message: string}>> {
    return this.http.post<SingleResult<{message: string}>>(
      `${environment.apiUrl}/api/einvoice/configuration/clear-cache`,
      {}
    );
  }

  /**
   * Get all payments for an incoming invoice
   * @param invoiceId - Incoming invoice ID
   * @returns Observable with payments and meta data
   */
  getInvoicePayments(invoiceId: string): Observable<SingleResult<{payments: EInvoicePayment[], meta: EInvoicePaymentMeta}>> {
    return this.http.get<SingleResult<{payments: EInvoicePayment[], meta: EInvoicePaymentMeta}>>(
      `${environment.apiUrl}/api/einvoice/incoming/${invoiceId}/payments`
    );
  }

  /**
   * Create a new payment for an incoming invoice
   * @param invoiceId - Incoming invoice ID
   * @param payment - Payment data (amount, type, date, note)
   * @returns Observable with created payment and updated invoice totals
   */
  createInvoicePayment(invoiceId: string, payment: {amount: number, type: string, date: string, note?: string}): Observable<SingleResult<{payment: EInvoicePayment, invoice_totals: EInvoicePaymentMeta}>> {
    return this.http.post<SingleResult<{payment: EInvoicePayment, invoice_totals: EInvoicePaymentMeta}>>(
      `${environment.apiUrl}/api/einvoice/incoming/${invoiceId}/payments`,
      payment
    );
  }

  /**
   * Update an existing payment
   * @param invoiceId - Incoming invoice ID
   * @param paymentId - Payment ID to update
   * @param payment - Updated payment data
   * @returns Observable with updated payment and invoice totals
   */
  updateInvoicePayment(invoiceId: string, paymentId: string, payment: {amount: number, type: string, date: string, note?: string}): Observable<SingleResult<{payment: EInvoicePayment, invoice_totals: EInvoicePaymentMeta}>> {
    return this.http.put<SingleResult<{payment: EInvoicePayment, invoice_totals: EInvoicePaymentMeta}>>(
      `${environment.apiUrl}/api/einvoice/incoming/${invoiceId}/payments/${paymentId}`,
      payment
    );
  }

  /**
   * Delete a payment (soft delete)
   * @param invoiceId - Incoming invoice ID
   * @param paymentId - Payment ID to delete
   * @returns Observable with updated invoice totals
   */
  deleteInvoicePayment(invoiceId: string, paymentId: string): Observable<SingleResult<{invoice_totals: EInvoicePaymentMeta}>> {
    return this.http.delete<SingleResult<{invoice_totals: EInvoicePaymentMeta}>>(
      `${environment.apiUrl}/api/einvoice/incoming/${invoiceId}/payments/${paymentId}`
    );
  }

  // ============================================================
  // Vehicle-expense linking (BRIEFD-6318)
  // ============================================================

  /**
   * Prepare prefill payload for the vehicle-expense form for a selected vehicle.
   * The backend creates an Upload from the invoice PDF and returns the prefill data.
   */
  prepareVehicleExpensePrefill(invoiceId: string, vehicleUuid: string): Observable<EInvoiceVehicleExpensePrefill> {
    return this.http.post<EInvoiceVehicleExpensePrefill>(
      `${environment.apiUrl}/api/einvoice/incoming/${invoiceId}/vehicle-expense-prefill`,
      {vehicle_uuid: vehicleUuid}
    );
  }

  /**
   * Record that a repeater item on a vehicle was created from this e-invoice
   * with a given allocated amount.
   */
  deleteVehicleExpenseLink(invoiceId: string, wrappedItemUuid: string): Observable<EInvoiceVehicleExpenseLinksResponse> {
    return this.http.delete<EInvoiceVehicleExpenseLinksResponse>(
      `${environment.apiUrl}/api/einvoice/incoming/${invoiceId}/vehicle-expense-links/${wrappedItemUuid}`
    );
  }

  recordVehicleExpenseLink(
    invoiceId: string,
    payload: {
      vehicle_uuid: string;
      wrapped_item_uuid: string;
      amount: number;
      expense_type_label?: string | null;
    }
  ): Observable<EInvoiceVehicleExpenseLinksResponse> {
    return this.http.post<EInvoiceVehicleExpenseLinksResponse>(
      `${environment.apiUrl}/api/einvoice/incoming/${invoiceId}/vehicle-expense-links`,
      payload
    );
  }

}

// ============================================================
// Types for vehicle-expense linking (BRIEFD-6318)
// ============================================================

export interface UploadAttachmentPayload {
  uploadUuid: string;
}

export interface EInvoiceVehicleExpensePrefill {
  invoice_number: string | null;
  invoice_date: string | null;
  net_amount: number | null;
  total_amount: number | null;
  suggested_title: string | null;
  attachment: UploadAttachmentPayload | null;
}

export interface EInvoiceVehicleExpenseLink {
  vehicle_uuid: string;
  vehicle_name: string;
  vehicle_vin_number: string | null;
  wrapped_item_uuid: string;
  amount: number;
  expense_type_label: string | null;
}

export interface EInvoiceVehicleExpenseLinksResponse {
  total_amount: number | null;
  remaining_amount: number;
  links: EInvoiceVehicleExpenseLink[];
}
